import { gql } from "@apollo/client";
import * as fragments from "@graphql/fragments";

// BEGIN POST WINDOWS
export const GetPostWindowsForUser = gql`
  query GetPostWindowsForUser(
    $targetUserId: ID
    $targetUsername: String
    $cursors: PostWindowsCursorInput
  ) {
    getPostWindowsForUser(
      targetUserId: $targetUserId
      targetUsername: $targetUsername
      cursors: $cursors
    ) @connection(key: "getPostWindowsForUser", filter: ["targetUserId"]) {
      postWindows {
        ...PostWindow
      }
      total
      currentVariables {
        targetUserId
        targetUsername
        cursors {
          offset
        }
      }
      nextVariables {
        targetUserId
        targetUsername
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.postWindow}
`;
// END POST WINDOWS

// BEGIN POSTS
export const GetActivePostWindow = gql`
  query GetActivePostWindow {
    getActivePostWindow @connection(key: "getActivePostWindow") {
      ...PostWindow
    }
  }
  ${fragments.postWindow}
`;

export const GetFeed = gql`
  query getFeed(
    $enableDrawingPosts: Boolean
    $enableTextPosts: Boolean
    $feedType: FeedType
    $targetUserId: ID
    $targetUsername: String
    $hashtagId: ID
    $contentType: ContentType
    $cursors: FeedCursorInput
  ) {
    getFeed(
      enableDrawingPosts: $enableDrawingPosts
      enableTextPosts: $enableTextPosts
      feedType: $feedType
      targetUserId: $targetUserId
      targetUsername: $targetUsername
      hashtagId: $hashtagId
      contentType: $contentType
      cursors: $cursors
    )
      # FIXME: Do we still need @connection now that we use keyArgs?
      # https://stackoverflow.com/questions/63305346/apollo-3-what-is-the-distinction-between-connection-and-keyargs
      @connection(
        key: "getFeed"
        filter: [
          "feedType"
          "targetUserId"
          "targetUsername"
          "hashtagId"
          "contentType"
        ]
      ) {
      posts {
        ...Post
      }
      total
      currentVariables {
        enableDrawingPosts
        enableTextPosts
        feedType
        targetUserId
        targetUsername
        hashtagId
        contentType
        cursors {
          offset
        }
      }
      nextVariables {
        enableDrawingPosts
        enableTextPosts
        feedType
        targetUserId
        targetUsername
        hashtagId
        contentType
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.post}
`;

export const GetPost = gql`
  query GetPost($id: ID) {
    getPost(id: $id) @connection(key: "getPost", filter: ["id"]) {
      ...Post
    }
  }
  ${fragments.post}
`;

export const GetPostIds = gql`
  query GetPostIds($cursors: FeedCursorInput) {
    getPostIds(cursors: $cursors) {
      posts {
        id
      }
      currentVariables {
        cursors {
          offset
        }
      }
      nextVariables {
        cursors {
          offset
        }
      }
    }
  }
`;

export const GetUserIds = gql`
  query GetUserIds($cursors: UserCursorInput) {
    getUserIds(cursors: $cursors) {
      users {
        id
      }
      currentVariables {
        cursors {
          offset
        }
      }
      nextVariables {
        cursors {
          offset
        }
      }
    }
  }
`;

// END POSTS
// BEGIN USERS
export const ValidateUsername = gql`
  query validateUsername($username: String!, $userId: ID) {
    validateUsername(username: $username, userId: $userId) {
      isValid
      validationMessage
    }
  }
`;

export const SearchUsers = gql`
  query searchUsers($keywords: String!, $cursors: SearchCursorInput) {
    searchUsers(keywords: $keywords, cursors: $cursors)
      @connection(key: "searchUsers", filter: ["keywords"]) {
      users {
        ...User
      }
      total
      currentVariables {
        keywords
        cursors {
          from
          size
        }
      }
      nextVariables {
        keywords
        cursors {
          from
          size
        }
      }
    }
  }
  ${fragments.user}
`;

export const SearchPosts = gql`
  query searchPosts($keywords: String!, $cursors: SearchCursorInput) {
    searchPosts(keywords: $keywords, cursors: $cursors)
      @connection(key: "searchPosts", filter: ["keywords"]) {
      posts {
        ...Post
      }
      total
      currentVariables {
        keywords
        cursors {
          from
          size
        }
      }
      nextVariables {
        keywords
        cursors {
          from
          size
        }
      }
    }
  }
  ${fragments.post}
`;

export const SearchHashtags = gql`
  query searchHashtags($keywords: String!, $cursors: SearchCursorInput) {
    searchHashtags(keywords: $keywords, cursors: $cursors)
      @connection(key: "searchHashtags", filter: ["keywords"]) {
      hashtags {
        ...Hashtag
      }
      total
      currentVariables {
        keywords
        cursors {
          from
          size
        }
      }
      nextVariables {
        keywords
        cursors {
          from
          size
        }
      }
    }
  }
  ${fragments.hashtag}
`;

export const GetUser = gql`
  query GetUser($targetUserId: ID, $targetUsername: String) {
    getUser(targetUserId: $targetUserId, targetUsername: $targetUsername)
      @connection(key: "getUser", filter: ["targetUserId", "targetUsername"]) {
      ...User
    }
  }
  ${fragments.user}
`;

export const GetMe = gql`
  query GetMe {
    getMe @connection(key: "getMe") {
      ...User
    }
  }
  ${fragments.user}
`;

export const GetBlockedUsers = gql`
  query GetBlockedUsers($cursors: UserCursorInput) {
    getBlockedUsers(cursors: $cursors) @connection(key: "getBlockedUsers") {
      users {
        ...User
      }
      total
      currentVariables {
        cursors {
          offset
        }
      }
      nextVariables {
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.user}
`;

export const GetUserFollowing = gql`
  query GetUserFollowing($targetUserId: ID!, $cursors: UserCursorInput) {
    getUserFollowing(targetUserId: $targetUserId, cursors: $cursors)
      @connection(key: "getUserFollowing", filter: ["targetUserId"]) {
      users {
        ...User
      }
      total
      currentVariables {
        targetUserId
        cursors {
          offset
        }
      }
      nextVariables {
        targetUserId
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.user}
`;

export const GetUserFollowers = gql`
  query GetUserFollowers($targetUserId: ID!, $cursors: UserCursorInput) {
    getUserFollowers(targetUserId: $targetUserId, cursors: $cursors)
      @connection(key: "getUserFollowers", filter: ["targetUserId"]) {
      users {
        ...User
      }
      total
      currentVariables {
        targetUserId
        cursors {
          offset
        }
      }
      nextVariables {
        targetUserId
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.user}
`;
// END USERS
// END COMMENTS
export const GetComment = gql`
  query getComment($id: ID) {
    getComment(id: $id) {
      ...Comment
    }
  }
  ${fragments.comment}
`;

export const GetComments = gql`
  query getComments(
    $postId: ID
    $commentId: ID
    $commentIds: [ID]
    $ancestor: Int
    $decendent: Int
    $maxDepth: Int
    $cursors: CommentsCursorInput
  ) {
    getComments(
      postId: $postId
      commentId: $commentId
      commentIds: $commentIds
      ancestor: $ancestor
      decendent: $decendent
      maxDepth: $maxDepth
      cursors: $cursors
    )
      @connection(
        key: "getComments"
        filter: ["postId", "commentId", "commentIds"]
      ) {
      comments {
        ...Comment
      }
      total
      moreReplies
      currentVariables {
        postId
        commentId
        commentIds
        ancestor
        decendent
        maxDepth
        cursors {
          commentsOffset
          repliesOffset
        }
      }
      nextVariables {
        postId
        commentId
        commentIds
        ancestor
        decendent
        maxDepth
        cursors {
          commentsOffset
          repliesOffset
        }
      }
    }
  }
  ${fragments.comment}
`;
export const GetUserComments = gql`
  query getUserComments($targetUserId: ID, $cursors: CommentsCursorInput) {
    getUserComments(targetUserId: $targetUserId, cursors: $cursors)
      @connection(key: "getUserComments", filter: ["targetUserId"]) {
      comments {
        ...Comment
      }
      total
      currentVariables {
        targetUserId
        cursors {
          commentsOffset
        }
      }
      nextVariables {
        targetUserId
        cursors {
          commentsOffset
        }
      }
    }
  }
  ${fragments.comment}
`;

// END COMMENTS
// BEGIN ALERTS
export const GetAlert = gql`
  query getAlert($id: ID) {
    getAlert(id: $id) @connection(key: "getAlert", filter: ["id"]) {
      ...Alert
    }
  }
  ${fragments.alert}
`;

export const GetAlerts = gql`
  query getAlerts($cursors: AlertsCursorInput) {
    getAlerts(cursors: $cursors) @connection(key: "getAlerts") {
      alerts {
        ...Alert
      }
      unreadAlertCount {
        ...UnreadAlertCount
      }
      total
      currentVariables {
        cursors {
          offset
        }
      }
      nextVariables {
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.alert}
  ${fragments.unreadAlertCount}
`;

export const GetAlertUsers = gql`
  query getAlertUsers($alertId: ID!, $cursors: UserCursorInput) {
    getAlertUsers(alertId: $alertId, cursors: $cursors)
      @connection(key: "getAlertUsers", filter: ["alertId"]) {
      users {
        ...User
      }
      total
      currentVariables {
        alertId
        cursors {
          offset
        }
      }
      nextVariables {
        alertId
        cursors {
          offset
        }
      }
    }
  }
  ${fragments.user}
`;
// END ALERTS
